import React, { useState, useEffect, useRef } from 'react';
import {
  getUserID,
  loadTitlesDB,
  loadWordsDB,
  monetizationUseCredits,
  saveData,
} from './helpers/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faCompress } from '@fortawesome/free-solid-svg-icons';
import History from './components/History';
import Analysis from './components/Analysis';
import SkimTest from './components/SkimTest';
import Social from './components/Social';
import logo from './img/logo.png';
import Formula from './components/Formula';
import Skills from './components/Skills';
import ValueProposition from './components/ValueProposition';
import Metrics from './components/Metrics';
import HeadlineLength from './components/HeadlineLength';
import useLocalStorage from './helpers/hooks/useLocalStorage';
import { JOB_TITLES, SKILLS } from './helpers/mockData';

const SKILL_TYPES = ['Hard Skill', 'Soft Skill'];
const DEV = false;
const MOCK_TITLE =
  'UX Designer | I help javascript companies write 10% better code';
const LOADER_PATH =
  'https://dev.cultivatedculture.com/wp-content/plugins/cc-headline-analyzer/assets/img/loader.gif';

const App = ({ ajax_url, post_text, loggedIn, userStatus }) => {
  const [loading, setLoading] = useState(false);
  const [credits, setCredits] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [headline, setHeadline] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [skills, setSkills] = useState([]);
  const [score, setScore] = useState(0);
  const [error, setError] = useState(null);
  const [headlineHistory, setHeadlineHistory] = useState([]);
  const [terms, setTerms] = useState([]);
  const [titles, setTitles] = useState([]);
  const [displayDate, setDisplayDate] = useLocalStorage('displayDate', '');
  const [displayFormula, setDisplayFormula] = useState(true);
  const [lengthValue, setLengthValue] = useState(0);
  const [monetizationCredits, setMonetizationCredits] = useState(0);
  const [monetizationLevel, setMonetizationLevel] = useState(0);
  const [isFieldExpanded, setIsFieldExpanded] = useState(false);

  const analysisRef = useRef();

  useEffect(() => {
    if (!DEV && loaded && (userStatus === '1' || loggedIn === 'false')) {
      sso();
    }
  }, [loaded]);

  useEffect(() => {
    const fetchMonetizationData = async () => {
      try {
        const user_id = await getUserID(ajax_url);
        const response = await fetch(
          '/wp-json/wp/v2/currentuserdata/' + user_id
        );

        if (response.ok) {
          const userData = await response.json();
          setMonetizationLevel(userData.monetization_level);
          setMonetizationCredits(userData.monetization_credits);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchMonetizationData();
  }, []);

  useEffect(() => {
    if (analysis) {
      scrollToAnalysis();
    }
  }, [analysis]);

  useEffect(() => {
    const onLoad = async () => {
      const [newTerms, newTitles] = await Promise.all([
        loadWordsDB(ajax_url),
        loadTitlesDB(ajax_url),
      ]);

      if (post_text !== '' && post_text !== undefined && post_text !== null) {
        setHeadline(post_text);
      }

      const filteredTerms = newTerms
        .filter(item => {
          if (SKILL_TYPES.includes(item.category)) {
            return item;
          }
        });

      if (!DEV) {
        setTerms(filteredTerms);
        setTitles(newTitles);
      }
    };

    if (DEV) {
      setTerms(SKILLS);
      setTitles(JOB_TITLES);
      setHeadline(MOCK_TITLE);
    }

    onLoad();
  }, []);

  useEffect(() => {
    if (new Date() < new Date(displayDate)) {
      setDisplayFormula(false);
    }
  }, [displayDate]);

  const scrollToAnalysis = () => {
    if (analysisRef.current) {
      analysisRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const analyzeHeadline = async e => {
    e.preventDefault();
    setLoading(true);
    setLoaded(false);

    let validCredits = true;
    if (!DEV) {
      try {
        const creditResponse = await monetizationUseCredits(ajax_url, setMonetizationCredits, 'headlineanalyzer', 2);
        console.log('creditResponse', creditResponse)
        if (creditResponse.action == 'limit' && loggedIn === 'true') {
          validCredits = false;
        }
      } catch (error) {
        console.error(error);
      }
    }
    if (!validCredits){
      return;
    }
    
    const analyzerPageContent = document.getElementsByClassName(
      'jsHeadlineAnalyzerContent'
    );
    for (let i = 0; i < analyzerPageContent.length; i++) {
      analyzerPageContent[i].classList.add('d-none');
    }

    const text = ` ${headline} `.toLowerCase();
    const percentRegex = /(\s[$€£¥₣₹]*[0-9.,]+\s*[%kKmM$€£¥₣₹+-x]*\s)/;
    const skillArr = [];
    const charLength = headline?.length;
    const wordLength = headline?.split(' ').length;
    let finalScore = 0;
    let lengthScore = 18;

    const skillNums = {
      numHardSkills: 0,
      numSoftSkills: 0,
    };

    const analysisObj = {
      valueProp: text.includes('i help'),
      title: titles.some(title => text.includes(` ${title} `)),
      skills: false,
      metrics: percentRegex.test(text),
      headlineLength:
        headline?.split(' ')?.length > 9 &&
        headline?.split(' ')?.length < 26 &&
        headline?.length > 74 &&
        headline?.length < 161,
    };

    let scoreCount = 0;
    for (let i = 0; i < terms.length; i++) {
      const term = terms[i].potential_term.toLowerCase();
      const idx =
        text.indexOf(` ${term} `) > -1
          ? text.indexOf(` ${term} `)
          : text.indexOf(` ${term},`);
      if (idx > -1) {
        const key = terms[i].category === 'Soft Skill' ? 'numSoftSkills' : 'numHardSkills';
        skillNums[key] = skillNums[key] + 1;
        const chunk = headline.slice(idx, term.length + idx);
        skillArr.push(chunk);

        if (scoreCount < 3) {
          finalScore += 9.33;
          scoreCount++;
        }
      }
    }

    if (skillArr.length > 2) analysisObj.skills = true;
    if (analysisObj.valueProp) finalScore += 18;
    if (analysisObj.title) finalScore += 18;
    if (analysisObj.metrics) finalScore += 18;
    if (
      (wordLength < 10 && wordLength > 4) ||
      (wordLength > 25 && wordLength < 31)
    ) {
      lengthScore -= 4;
    }

    if ((wordLength < 5 && wordLength > 0) || wordLength > 30) {
      lengthScore -= 6;
    }

    if (
      (charLength < 75 && charLength > 59) ||
      (charLength > 159 && charLength < 191)
    ) {
      lengthScore -= 4;
    }

    if ((charLength < 60 && charLength > 0) || charLength > 190) {
      lengthScore -= 6;
    }

    if (!charLength || !wordLength) {
      lengthScore = 0;
    }

    if (lengthScore > 0) finalScore += lengthScore;

    const result = Math.round(finalScore) > 99 ? 100 : Math.round(finalScore);

    const dbObj = {
      text: headline,
      score: result,
      num_words: wordLength,
      num_characters: charLength,
      job_title: analysisObj.title,
      metrics_present: analysisObj.metrics,
      num_hard_skills: skillNums.numHardSkills,
      num_soft_skills: skillNums.numSoftSkills,
    };

    await saveData(dbObj, ajax_url);

    setAnalysis(analysisObj);
    setSkills(skillArr);
    setScore(result);
    setLengthValue(lengthScore);
    setHeadlineHistory(prevState => [
      ...prevState,
      { headline, score: result },
    ]);
    setLoaded(true);
    setLoading(false);

    scrollToAnalysis();
  };

  const handleButtonPress = input => {
    setDisplayDate(input);
  };

  const handleChangeText = e => {
    setHeadline(e.target.value);
  };

  const toggleFieldExpansion = () => {
    setIsFieldExpanded(!isFieldExpanded);
  };

  const sso = () => {
    const haAnalyzer = document.getElementById('haAnalyzer');
    const userStatus = haAnalyzer.getAttribute('user_status');
    if (userStatus === '0') {
      const userModal = document.getElementsByClassName('userModal');
      for (let i = 0; i < userModal.length; i++) {
        userModal[i].style.display = 'block';
        // userModal[i].append(scoreChart)
      }
      const loginModalForm = document.getElementById('jsLoginModalForm');
      loginModalForm.classList.add('d-none');
      const signupModalForm = document.getElementById('jsSignupModalForm');
      signupModalForm.classList.remove('d-none');
      const modalContent = document.getElementsByClassName('jsModalContent');
      for (let i = 0; i < modalContent.length; i++) {
        modalContent[i].classList.add('d-none');
      }
      const modalBullet = document.getElementsByClassName('jsModalHeadline');
      for (let i = 0; i < modalBullet.length; i++) {
        modalBullet[i].classList.remove('d-none');
      }
      const bulletScore = document.getElementById('jsModalHeadlineScore');
      bulletScore.innerHTML = `${score} / 100`;
    } else {
      const verifyModal = document.getElementsByClassName('verifyModal');
      for (let i = 0; i < verifyModal.length; i++) {
        verifyModal[i].style.display = 'block';
      }
      const modalContent = document.getElementsByClassName('jsModalContent');
      for (let i = 0; i < modalContent.length; i++) {
        modalContent[i].classList.add('d-none');
      }
      const verifyTool = document.getElementsByClassName('jsVerifyTool');
      for (let i = 0; i < verifyTool.length; i++) {
        verifyTool[i].classList.remove('d-none');
      }
    }

    setLoaded(false);
  };

  return (
    <div className="app-container">
      <div className="d-flex align-items-center justify-content-center hero">
        <form onSubmit={analyzeHeadline} className="haform" id="form">
          <div className="logo d-flex justify-content-center">
            <img src={logo} alt="HeadlineAnalyzer.io" />
          </div>
          <div className="px-sm-5 mt-4">
            <h1 className="font-weight-bold text-center">
              <span className="text-primary">FREE</span> LinkedIn Headline
              Analyzer
            </h1>
            <h4 className="text-center my-4">
              Learn how to write a crazy effective LinkedIn Headline that will
              help you generate more job interviews.
            </h4>
          </div>
          <div className="w-100 d-flex flex-column align-items-center">
            <div className="d-flex flex-column align-items-center w-sm-75">
              <p className="text-center pb-sm-3 font-weight-bold">
                <span className="font-override">
                  Paste your LinkedIn Headline to begin:
                </span>
              </p>
              {error && <div className="haform-error">{error}</div>}

              <div className={`headline-input-container ${isFieldExpanded ? 'expanded' : ''}`}>
                {isFieldExpanded ? (
                  <textarea
                    className="headline-input headline-textarea"
                    value={headline}
                    onChange={handleChangeText}
                    placeholder="Enter your LinkedIn Headline here"
                    rows="4"
                  />
                ) : (
                  <input
                    type="text"
                    className="headline-input"
                    value={headline}
                    onChange={handleChangeText}
                    placeholder="Enter your LinkedIn Headline here"
                  />
                )}
                <button
                  type="button"
                  className="field-expander-btn"
                  onClick={toggleFieldExpansion}
                  title={isFieldExpanded ? "Collapse to single line" : "Expand to multi-line"}
                >
                  <FontAwesomeIcon icon={isFieldExpanded ? faCompress : faExpand} />
                </button>
              </div>
              <button
                className="d-flex align-items-center justify-content-center btn btn-danger mt-4 mt-sm-5"
                onClick={analyzeHeadline}
                disabled={!terms[0] || headline.length < 10}
              >
                {loading ? (
                  <span>
                    <img
                      className="loading"
                      src={LOADER_PATH}
                      alt="Loading..."
                    />
                  </span>
                ) : (
                  <span className="font-override row">Click To Analyze!</span>
                )}
              </button>
            </div>

            {monetizationLevel == 'free' && monetizationCredits > 0 && (
              <p className="small mt-2">
                <span className="font-override">
                  You Have {Math.floor(monetizationCredits/2)} Free Headline Scans Left{' '}
                  <a
                    href="https://cultivatedculture.crunch.help/en/my-account/what-are-cultivated-culture-credits"
                    target="_blank"
                  >
                    (Learn More)
                  </a>
                </span>
              </p>
            )}
            {monetizationLevel !== 'free' && monetizationCredits > 0 && (
              <p className="small mt-2">
                <span className="font-override">
                  You Have Unlimited Free Headline Scans{' '}
                  <a
                    href="https://cultivatedculture.crunch.help/en/my-account/what-are-cultivated-culture-credits"
                    target="_blank"
                  >
                    (Learn More)
                  </a>
                </span>
              </p>
            )}
          </div>
        </form>
      </div>

      {((analysis !== null && loggedIn === 'true' && userStatus === '0') ||
        DEV) && (
        <>
          <div ref={analysisRef} className="hastatus-bar">
            <h5 className="hacont text-light">Analysis Complete!</h5>
          </div>

          <div>
            <History headlineHistory={headlineHistory} />
            <Analysis
              score={score}
              analysis={analysis}
              headline={headline}
              skills={skills}
              terms={terms}
              titles={titles}
            />
            {displayFormula && <Formula onButtonPress={handleButtonPress} />}
            <Skills skills={skills} displayFormula={displayFormula} />
            <ValueProposition
              analysis={analysis}
              displayFormula={displayFormula}
            />
            <Metrics
              analysis={analysis}
              headline={headline}
              displayFormula={displayFormula}
            />
            <HeadlineLength
              analysis={analysis}
              headline={headline}
              displayFormula={displayFormula}
            />
            <SkimTest
              analysis={analysis}
              headline={headline}
              displayFormula={displayFormula}
            />
            {score > 0 && (
              <Social
                analysis={analysis}
                skills={skills}
                displayFormula={displayFormula}
                lengthValue={lengthValue}
                score={score}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default App;
